import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import CustomButton from "components/common/Button/CustomButton";
import ConfirmationModal from "components/common/Modal/ConfirmationModal";
import { apiFormDataRequest } from "helpers/Requests";

export const PDF_IMPORT_MAX_BYTES = 10 * 1024 * 1024;

export const resolvePdfImportEndpoint = ({
  isSuperAdmin = false,
  role = "",
  routeRole = "",
}) => {
  if (isSuperAdmin) {
    return role === "hrm"
      ? "super-admin/hrm/form-template/import-pdf"
      : "super-admin/form-template/import-pdf";
  }

  if (role === "sil" || routeRole === "sil") {
    return "sil/form/import-pdf";
  }

  if (role === "hrm" || routeRole === "sp/hrm") {
    return "sp/hrm/form-template/import-pdf";
  }

  return "sp/form-template/import-pdf";
};

const PDF_KIND_LABELS = {
  interactive: "interactive",
  text: "text",
  scanned: "scanned",
};

const PdfFormImportButton = ({
  endpoint,
  hasExistingFields = false,
  onImported,
}) => {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const uploadPdf = async (file) => {
    const body = new FormData();
    body.append("file", file);
    setLoading(true);

    try {
      const response = await apiFormDataRequest(endpoint, body);
      const draft = response?.data?.data;
      if (!draft?.template?.length) {
        toast.error(
          draft?.warnings?.[0] ||
            "No fields could be read from this PDF. If it is a scan, use a clearer digital PDF or add the fields on the canvas."
        );
        return;
      }

      onImported?.(draft);
      const kind = PDF_KIND_LABELS[draft.document_kind] || "PDF";
      toast.success(
        `Draft form created from this ${kind} PDF. Review it on the canvas before you save or publish.`
      );
      (draft.warnings || []).slice(0, 2).forEach((warning) => toast.info(warning));
    } catch (error) {
      if (!error?.isCancelled) {
        toast.error(error?.message || "Could not read that PDF.");
      }
    } finally {
      setLoading(false);
      setPendingFile(null);
      setShowReplaceModal(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const lowerName = file.name.toLowerCase();
    const allowed =
      lowerName.endsWith(".pdf") ||
      lowerName.endsWith(".jpg") ||
      lowerName.endsWith(".jpeg") ||
      lowerName.endsWith(".png");
    if (!allowed) {
      toast.error("Upload a PDF or scan image (JPEG/PNG).");
      return;
    }

    if (file.size > PDF_IMPORT_MAX_BYTES) {
      toast.error("The file must be 10 MB or smaller.");
      return;
    }

    if (hasExistingFields) {
      setPendingFile(file);
      setShowReplaceModal(true);
      return;
    }

    await uploadPdf(file);
  };

  const closeReplaceModal = () => {
    if (loading) return;
    setShowReplaceModal(false);
    setPendingFile(null);
  };

  const confirmReplace = () => {
    if (!pendingFile || loading) return;
    uploadPdf(pendingFile);
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf,image/jpeg,.jpg,.jpeg,image/png,.png"
        hidden
        onChange={handleFile}
      />
      <CustomButton
        type="button"
        variant="secondary"
        cssClass="form-btn-outline pdf-import-btn"
        label={
          loading ? (
            "Reading PDF..."
          ) : (
            <span className="pdf-import-btn-label">
              Import PDF
              <span
                className="pdf-import-beta-badge"
                title="Beta — PDF import is new and will keep improving"
              >
                Beta
              </span>
            </span>
          )
        }
        showLoading={loading}
        loaderCssClass="pdf-import-btn-loader"
        isDisabled={loading}
        aria-busy={loading}
        clickHandler={() => inputRef.current?.click()}
      />

      <ConfirmationModal
        isNew
        showModal={showReplaceModal}
        title="Replace form canvas?"
        cancelBtnLabel="Keep current form"
        confirmBtnLabel={loading ? "Reading PDF..." : "Replace with PDF draft"}
        variantConfirmBtn="danger"
        confirmBtnLoader={loading}
        closeModalHandler={closeReplaceModal}
        submitHandler={confirmReplace}
        modalCssClass="w-[92%] max-w-lg sm:max-w-xl z-[99999]"
      >
        <div className="pdf-import-replace-modal space-y-4">
          <p className="text-sm leading-relaxed text-gray-700">
            Importing a PDF will replace the fields currently on this canvas with
            a draft generated from the document. Your form title and description
            stay as they are unless they are still empty.
          </p>

          {pendingFile && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Selected file
              </p>
              <p className="mt-1 text-sm font-medium text-gray-900 break-all">
                {pendingFile.name}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {(pendingFile.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            </div>
          )}

          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Review the imported draft before publishing. Layout, tables, and
            scanned PDFs may need manual cleanup. Use <strong>Discard changes</strong>{" "}
            afterward if you want to restore the form from before this import.
          </div>
        </div>
      </ConfirmationModal>
    </>
  );
};

export default PdfFormImportButton;
